import _ from 'lodash';
import {createSelector} from 'reselect';
import {controlType} from '../../util/uiconstants';

export const getBodyMetadataSlice = (state, bodyId) => state.bodiesSpecial.metadata[bodyId];

/**
 * Gets the Bodies Special IT Applications sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @returns {Object} The Bodies Special IT Applications sub-splice.
 */
export const getItApplicationsSlice = (state) => state.bodiesSpecial?.itApplications;

/**
 * Gets the Bodies Special Risk Factors sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @returns {Object} The Bodies Special Risk Factors sub-splice.
 */
export const getRiskFactorsSlice = (state) => state.bodiesSpecial?.riskFactors || {};

const getRiskFactorsByIdSlice = (state) => getRiskFactorsSlice(state).byId || {};

/**
 * Given a Redux store and a riskFactorId, returns a risk factor's data.
 * @param {state} state The Redux state object.
 * @param {riskFactorId} riskFactorId Risk factor id.
 * @returns Risk factor details
 */
export const getRiskFactorById = (state, riskFactorId) => getRiskFactorsByIdSlice(state)[riskFactorId];

/**
 * Gets the Bodies Special Risks sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @returns {Object} The Bodies Special Risks sub-splice.
 */
export const getRisksSlice = (state) => state.bodiesSpecial?.risks;

/**
 * Retrieves the specified IT Application object by its ID from the Bodies Special IT Applications sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @param {Number} itApplicationId The IT Application ID.
 * @returns {Object} The specified IT Application object.
 */
export const getItApplicationById = (state, itApplicationId) =>
	state.bodiesSpecial?.itApplications?.byId[itApplicationId];

/**
 * Retrieves every IT Application present in the Bodies Special IT Applications sub-splice of the Redux Store.
 * @returns {Array<Object>} All of the IT Application present in the Bodies Special IT Applications sub-splice of the Redux Store.
 */
export const getAllItApplications = createSelector([getItApplicationsSlice], (itApplications) => {
	return itApplications?.allIds?.map((itApplicationId) => itApplications.byId[itApplicationId]);
});

/**
 * Gets all of the IDs of the IT Process in the Bodies Special splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @returns {Object} All of the IDs of the IT Processes in the Bodies Special splice of the Redux Store
 */
export function getITProcessesAllIds(state) {
	return {
		allIds: state.bodiesSpecial.itProcesses?.allIds,
		totalEntityCount: state.bodiesSpecial.itProcesses?.totalEntityCount
	};
}

export function getITProcessById(state, itProcessId) {
	return state.bodiesSpecial?.itProcesses?.byId[itProcessId];
}

/**
 * Gets the Bodies Special Controls sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @returns {Object} The Bodies Special Controls sub-splice.
 */
export const getControlsSlice = (state) => state.bodiesSpecial?.controls;

/**
 * Retrieves every Control present in the Bodies Special Controls sub-splice of the Redux Store.
 * @returns {Array<Object>} All of the Control present in the Bodies Special Controls sub-splice of the Redux Store.
 */
export const getAllControls = createSelector([getControlsSlice], (controls) => {
	return controls?.allIds?.map((controlId) => controls.byId[controlId]);
});

/**
 * Gets the Bodies Special SCOTs sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @returns {Object} The Bodies Special Controls sub-splice.
 */
export const getSCOTSlice = (state) => state.bodiesSpecial?.scots;

/**
 * Retrieves every SCOT present in the Bodies Special SCOTs sub-splice of the Redux Store.
 * @returns {Array<Object>} All of the SCOTs present in the Bodies Special SCOTs sub-splice of the Redux Store.
 */
export const getAllSCOTs = createSelector([getSCOTSlice], (scots) => {
	return scots?.allIds?.map((scotId) => scots.byId[scotId]);
});

/**
 * Gets the Bodies Special IT risks sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @returns {Object} The Bodies Special IT risks sub-splice.
 */
export const getItRiskSlice = (state) => state.bodiesSpecial.itRisks;

/**
 * Retrieves every IT risk present in the Bodies Special IT risks sub-splice of the Redux Store.
 * @returns {Array<Object>} All of the IT risks present in the Bodies Special IT risks sub-splice of the Redux Store.
 */
export const getAllItRisks = createSelector([getItRiskSlice], (itRisks) => {
	return itRisks?.allIds?.map((itRiskId) => itRisks.byId[itRiskId]);
});

/**
 * Gets the Bodies Special IT risks sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @returns {Object} The Bodies Special IT risks sub-splice.
 */
export const getItControlsSlice = (state) => state.bodiesSpecial.itControls;

/**
 * Retrieves every IT risk present in the Bodies Special IT risks sub-splice of the Redux Store.
 * @returns {Array<Object>} All of the IT risks present in the Bodies Special IT risks sub-splice of the Redux Store.
 */
export const getAllItControls = createSelector([getItControlsSlice], (itControls) => {
	return itControls?.allIds?.map((itControlId) => itControls.byId[itControlId]);
});

/**
 * Retrieves the specified Scot object by its ID from the Bodies Special Scot sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @param {Number} ScotId The Scot ID.
 * @returns {Object} The specified Scot object.
 */
export const getScotById = (state, ScotId) => state.bodiesSpecial?.scots?.byId[ScotId];

/**
 * Retrieves the specified Control object by its ID from the Bodies Special Control sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @param {Number} controlId The Control ID.
 * @returns {Object} The specified Control object.
 */
export const getControlById = (state, controlId) => state.bodiesSpecial?.controls?.byId[controlId];

/**
 * Retrieves the specified itControl object by its ID from the Bodies Special Control sub-splice of the Redux Store.
 * @param {Object} state The Redux State object.
 * @param {Number} itControlId The itControl ID.
 * @returns {Object} The specified Control object.
 */
export const getitControlById = (state, itControlId) => state.bodiesSpecial?.itControls?.byId[itControlId];

export const getControlsAccumulator = (state, scot, itApplicationsSlice, controlsSlice) => {
	const result = {
		//The ammount of itApps related to the scot through controls.
		count: 0,
		//The it apps related to the scot through controls but not directly related to the scot.
		relatedItApps: [],

		//The controls which do not have any it app related to it
		controlsWithNoItApps: {
			all: [],
			//The number of application controls inside "list"
			[controlType.itApplicationControl]: [],
			//The number of ITDM controls inside "list"
			[controlType.itDependentManualControl]: []
		}
	};

	scot.controls.forEach((controlId) => {
		const control = controlsSlice.byId[controlId];

		if (
			control.itApplications?.length > 0 &&
			control.itApplications.some((id) => !itApplicationsSlice.byId[id].isServiceOrganization)
		) {
			control.itApplications.forEach((itApplicationId) => {
				if (!result[itApplicationId] && !itApplicationsSlice.byId[itApplicationId].isServiceOrganization) {
					{
						result.count++;
						result[itApplicationId] = true;
						if (scot.itApplications.indexOf(itApplicationId) === -1) {
							result.relatedItApps.push(itApplicationId);
						}
					}
				}
			});
		} else if (result.controlsWithNoItApps[control.controlTypeId]) {
			result.controlsWithNoItApps.all.push(controlId);
			result.controlsWithNoItApps[control.controlTypeId].push(controlId);
		}
	});

	return result;
};

export const getScotControls = (state, scotId) =>
	state.bodiesSpecial.scots.byId[scotId].controls.filter(
		(controlId) =>
			state.bodiesSpecial.controls.byId[controlId].controlTypeId === controlType.itApplicationControl ||
			state.bodiesSpecial.controls.byId[controlId].controlTypeId === controlType.itDependentManualControl
	);

export const getRiskFactorGroups = createSelector([getRiskFactorsSlice], (riskFactors) => {
	const riskFactorsList = Object.values(riskFactors?.byId || {});
	let riskFactorByFormHeaders = {};

	if (riskFactorsList && riskFactorsList.length > 0) {
		riskFactorByFormHeaders = _.chain(riskFactorsList)
			// Group the elements of Array based on `formheaderName` property
			.groupBy('formHeaderName')
			// `key` is group's name (formHeaderName), `value` is the array of objects (risks factors)
			.map((value, key) => {
				return {
					formHeaderId: value[0].formHeaderId,
					formHeaderTitle: `${value[0].taskShortName} - ${value[0].formHeaderName}`,
					formHeaderName: value[0].formHeaderName, // To be deprecated!!
					taskDisplayOrder: value[0].taskDisplayOrder,
					riskFactors: value,
					riskFactorsList: value.map((rf) => rf.id)
				};
			})
			.value();
	}
	const orderedRiskFactorResult = _.orderBy(riskFactorByFormHeaders, ['taskDisplayOrder', 'formHeaderId'], ['asc']);
	return orderedRiskFactorResult;
});

// Returns an object with riskFactorId as key and editable properties as content for each risk factor
export const getRiskFactorsEditionState = createSelector([getRiskFactorsByIdSlice], (riskFactorsById) => {
	let riskFactorsState = {};
	Object.values(riskFactorsById).forEach((riskFactor) => {
		riskFactorsState[riskFactor.id] = {
			rationale: riskFactor.rationale
		};
	});

	return riskFactorsState;
});
